import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { LoggerService } from './logger.service';

/**
 * Logs Angular router navigation events as performance‑timing log entries.
 *
 * Captured metrics:
 *  - navigationStart → navigationEnd duration
 *  - URL, correlationId
 *
 * Use: inject in a service or component that is created early (e.g. AppComponent).
 */
@Injectable({ providedIn: 'root' })
export class NavigationTimingService {
  private readonly router = inject(Router);
  private readonly logger = inject(LoggerService);

  private navStart = 0;

  /** Start listening. Call once from AppComponent or bootstrap. */
  init(): void {
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
    ).subscribe((event: NavigationEnd) => {
      if (this.navStart === 0) {
        // First navigation; use performance.timing if available
        this.logFirstNavigation(event);
        return;
      }

      const durationMs = Math.round((performance.now() - this.navStart) * 100) / 100;
      this.logger.debug('Router', `Navigation to "${event.urlAfterRedirects}" completed`, {
        url: event.urlAfterRedirects,
        durationMs,
        navigationId: event.id,
      });

      this.navStart = 0;
    });

    // Capture navigation start
    this.router.events.subscribe((e) => {
      if (e.constructor.name === 'NavigationStart') {
        this.navStart = performance.now();
      }
    });
  }

  private logFirstNavigation(event: NavigationEnd): void {
    // Use Navigation Timing API Level 2 when available
    if (typeof performance !== 'undefined' && performance.getEntriesByType) {
      const navEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
      if (navEntries.length > 0) {
        const nav = navEntries[0];
        this.logger.info('Router', `Initial page load complete`, {
          url: event.urlAfterRedirects,
          domContentLoadedMs: Math.round(nav.domContentLoadedEventEnd - nav.startTime),
          loadCompleteMs: Math.round(nav.loadEventEnd - nav.startTime),
          ttfbMs: Math.round(nav.responseStart - nav.startTime),
          dnsMs: Math.round(nav.domainLookupEnd - nav.domainLookupStart),
          tcpMs: Math.round(nav.connectEnd - nav.connectStart),
          navigationId: event.id,
        });
        return;
      }
    }

    // Fallback
    const timing = performance?.timing;
    if (timing) {
      this.logger.info('Router', `Initial page load complete (legacy timing)`, {
        url: event.urlAfterRedirects,
        loadTimeMs: timing.loadEventEnd - timing.navigationStart,
        navigationId: event.id,
      });
    } else {
      this.logger.info('Router', `Navigation to "${event.urlAfterRedirects}" completed`, {
        url: event.urlAfterRedirects,
        navigationId: event.id,
      });
    }
  }
}
